import { FormControlLabel, Switch } from '@mui/material';
import { selectedProductsContext } from 'pages/managePricing/managePricing';
import { AccountNumberParams } from 'pages/managePricing/managePricingResolver';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProductFilterEnum } from 'shared/constants';
import { useFetchTableData } from 'shared/hooks/use-fetch-table-data';
import { useSelectedItems } from 'shared/providers/provide-selected-items';
import { TableDataContext } from 'shared/types';
import { TAddProductsData } from 'types/backend';

interface IProductsToggle {
    productTabSelected: ProductFilterEnum;
    tableDataContext: React.Context<TableDataContext<TAddProductsData>>;
}

export function ProductsToggle({ productTabSelected, tableDataContext }: IProductsToggle) {
    const { accountNumber } = useParams<AccountNumberParams>();

    const [checked, setChecked] = useState<boolean>(false);
    const { selectedItems, updateItems } = useSelectedItems(selectedProductsContext);

    const { filtersApplied } = useContext(tableDataContext);

    // we need to get all products to select them all
    const response = useFetchTableData<TAddProductsData>({
        cacheName: `all${productTabSelected}Products`,
        queryPath: `${productTabSelected}Product/subset/${accountNumber}/all`,
        filtersApplied,
    });

    const products = useMemo(() => response?.data?.rows || [], [response]);
    const areAllProductsLoading = !!(response?.isLoading || response?.isFetching);

    const filterBy = useMemo(
        () =>
            productTabSelected === ProductFilterEnum.EXCLUSIVE
                ? 'isexclusiveflag'
                : productTabSelected === ProductFilterEnum.PRIVATE
                ? 'isprivateflag'
                : 'iscatalogflag',
        [productTabSelected]
    );

    const filteredProducts = useMemo(
        () => selectedItems.filter((item) => Boolean(item[filterBy])),
        [selectedItems, filterBy]
    );

    const handleChange = () => {
        if (checked) {
            clearItemsByFilter();
        } else {
            selectAllItemsByFilter();
        }
        setChecked(!checked);
    };

    const clearItemsByFilter = () => {
        updateItems(selectedItems.filter((item) => !Boolean(item[filterBy])));
    };

    const selectAllItemsByFilter = () => {
        const selectedProducts = products.reduce((prods: TAddProductsData[], product) => {
            if (!filteredProducts.find((p) => p.id === product.id)) {
                // avoid adding duplicates
                prods.push({
                    ...product,
                    price: product.targetprice,
                });
            }
            return prods;
        }, []);
        updateItems([...selectedItems, ...selectedProducts]);
    };

    useEffect(() => {
        if (!areAllProductsLoading && products?.length > 0) {
            if (products.every((product) => filteredProducts.find((p) => p.id === product.id))) {
                setChecked(true);
            }
        }
    }, [areAllProductsLoading, products, filteredProducts]);

    return (
        <FormControlLabel
            control={<Switch checked={checked} onChange={handleChange} disabled={areAllProductsLoading} />}
            label={`Select All ${productTabSelected} Products`}
        />
    );
}
